export type OperationFieldOption = string | { value: string; label: string }

export interface OperationField {
  key?: string
  name?: string
  label: string
  kind: string
  required?: boolean
  hidden?: boolean
  wide?: boolean
  maxLength?: number
  options?: readonly OperationFieldOption[]
  fields?: readonly OperationField[]
  visibleWhen?: {
    path: string
    value: string
  }
  /** Upload purpose for asset/asset-list fields, used by AssetUploader. */
  assetPurpose?: string
}

export type OperationValues = Record<string, unknown>

export function normalizeOperationValues(
  fields: readonly OperationField[],
  submitted: OperationValues,
  previous: OperationValues = {},
) {
  const values = cloneValues(previous)
  for (const field of fields) normalizeSubmittedField(values, submitted, field, '')
  return values
}

export function operationFieldVisible(field: OperationField, values: OperationValues) {
  if (!field.visibleWhen) return true
  return readPath(values, field.visibleWhen.path) === field.visibleWhen.value
}

function normalizeSubmittedField(
  target: OperationValues,
  submitted: OperationValues,
  field: OperationField,
  prefix: string,
) {
  if (!operationFieldVisible(field, submitted)) return
  if (field.hidden) return
  const key = fieldKey(field)
  if (!key) return
  const path = prefix ? `${prefix}.${key}` : key
  if (field.kind === 'group') {
    for (const nested of field.fields || []) normalizeSubmittedField(target, submitted, nested, path)
    return
  }
  const raw = readPath(submitted, path)
  if (field.kind === 'checkbox' || field.kind === 'boolean') {
    writePath(target, path, raw === true)
    return
  }
  if (field.kind === 'multi-select') {
    writePath(target, path, Array.isArray(raw) ? raw.map(String).filter(Boolean) : [])
    return
  }
  if (['id-list', 'profile-ref-list', 'tags'].includes(field.kind)) {
    writePath(target, path, splitLines(String(raw || '')))
    return
  }
  if (field.kind === 'asset-list') {
    writePath(target, path, normalizeAssetList(raw, readPath(target, path)))
    return
  }
  if (field.kind === 'date' && raw && typeof raw === 'object'
    && 'format' in raw && typeof raw.format === 'function') {
    writePath(target, path, raw.format('YYYY-MM-DD'))
    return
  }
  if (['datetime', 'datetime-local'].includes(field.kind)
    && raw && typeof raw === 'object' && 'toISOString' in raw
    && typeof raw.toISOString === 'function') {
    writePath(target, path, raw.toISOString())
    return
  }
  writePath(target, path, raw ?? '')
}

function fieldKey(field: OperationField) {
  return String(field.name || field.key || '').trim()
}

function splitLines(value: string) {
  return value.split(/[\n,，]/).map(item => item.trim()).filter(Boolean)
}

function normalizeAssetList(value: unknown, previous: unknown) {
  const previousItems = assetListItems(previous)
  const previousById = new Map<string, OperationValues>()
  for (const item of previousItems) {
    const assetId = assetIdFrom(item)
    if (assetId && !previousById.has(assetId)) previousById.set(assetId, recordValue(item))
  }

  const submittedItems = typeof value === 'string'
    ? splitLines(value)
    : Array.isArray(value) ? value : []
  const seen = new Set<string>()
  const normalized: OperationValues[] = []
  for (const item of submittedItems) {
    const assetId = assetIdFrom(item)
    if (!assetId || seen.has(assetId)) continue
    seen.add(assetId)

    const submittedItem = recordValue(item)
    const previousItem = previousById.get(assetId)
    const caption = Object.hasOwn(submittedItem, 'caption')
      ? String(submittedItem.caption ?? '')
      : String(previousItem?.caption ?? '')
    normalized.push({ assetId, caption })
  }
  return normalized
}

function assetListItems(value: unknown) {
  if (typeof value === 'string') return splitLines(value)
  return Array.isArray(value) ? value : []
}

function assetIdFrom(value: unknown) {
  const candidate = typeof value === 'string' ? value : recordValue(value).assetId
  return String(candidate ?? '').trim()
}

function readPath(value: OperationValues, path: string) {
  return path.split('.').reduce<unknown>((current, key) => recordValue(current)[key], value)
}

function writePath(value: OperationValues, path: string, next: unknown) {
  const parts = path.split('.')
  let current = value
  for (const part of parts.slice(0, -1)) {
    const child = recordValue(current[part])
    current[part] = child
    current = child
  }
  current[parts.at(-1)!] = next
}

function recordValue(value: unknown): OperationValues {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as OperationValues : {}
}

function cloneValues(value: OperationValues): OperationValues {
  return typeof structuredClone === 'function'
    ? structuredClone(value)
    : JSON.parse(JSON.stringify(value)) as OperationValues
}
